import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { EditorState } from '@atlaskit/editor-prosemirror/state';

import type { AiAutocompletePlugin } from '../../aiAutocompletePluginType';
import type { AutoTriggerSharedState } from '../../pm-plugins/shared-auto-trigger-state';
import { ACTIONS, type AiAutocompletePluginState } from '../types/types';
import { addAutocompleteViewedAnalytics } from '../utils/analytics';
import { callHeadingAutocompleteApi } from '../utils/api/headingAutocompleteApi';
import { getDocumentPlainText } from '../utils/charAndWordCount';
import { get1BasedLineIndexFromDocStart } from '../utils/docRegionCheck';
import { serializeDocWithCursorMarker } from '../utils/markdownSerializer';

import { buildSuggestionDecoration } from './decorations';
import { aiAutocompletePluginKey } from './plugin-key';

type PluginParams = {
	api: ExtractInjectionAPI<AiAutocompletePlugin> | undefined;
	autoTriggerSharedState: AutoTriggerSharedState;
};

export const createPlugin = ({
	api,
	autoTriggerSharedState,
}: PluginParams): SafePlugin<AiAutocompletePluginState> => {
	let abortController: AbortController | null = null;
	const editorAnalyticsAPI = api?.analytics?.actions;

	return new SafePlugin({
		key: aiAutocompletePluginKey,
		// -----------------------------------------------------------------------
		// Plugin state — pure synchronous transitions
		// -----------------------------------------------------------------------
		state: {
			init(): AiAutocompletePluginState {
				return { status: 'idle' };
			},

			apply(tr, pluginState): AiAutocompletePluginState {
				const action = tr.getMeta(aiAutocompletePluginKey);
				if (action) {
					switch (action.type) {
						case ACTIONS.TRIGGER_MANUAL:
							return {
								status: 'loading',
								trigger: 'manual',
								cursorPos: action.cursorPos,
							};
						case ACTIONS.TRIGGER_AUTO:
							return {
								status: 'loading',
								trigger: 'auto',
								cursorPos: action.cursorPos,
							};
						case ACTIONS.SET_SUGGESTION:
							if (pluginState.status === 'loading') {
								if (pluginState.trigger === 'auto') {
									if (
										'displaySuggestionImmediately' in action &&
										action.displaySuggestionImmediately === true
									) {
										const cursorPos =
											'suggestionCursorPos' in action && action.suggestionCursorPos !== undefined
												? action.suggestionCursorPos
												: pluginState.cursorPos;
										return {
											status: 'suggestion',
											trigger: 'auto',
											cursorPos,
											suggestion: action.suggestion,
										};
									}
									return {
										status: 'suggestion_pending',
										trigger: 'auto',
										cursorPos: pluginState.cursorPos,
										suggestion: action.suggestion,
									};
								}
								return {
									status: 'suggestion',
									trigger: 'manual',
									cursorPos: pluginState.cursorPos,
									suggestion: action.suggestion,
								};
							}
							return pluginState;
						case ACTIONS.REVEAL_SUGGESTION:
							if (pluginState.status === 'suggestion_pending') {
								const revealCursorPos =
									'cursorPos' in action && action.cursorPos !== undefined
										? action.cursorPos
										: pluginState.cursorPos;
								return {
									status: 'suggestion',
									trigger: 'auto',
									cursorPos: revealCursorPos,
									suggestion: pluginState.suggestion,
								};
							}
							return pluginState;
						case ACTIONS.DISMISS:
							return { status: 'idle' };
						default:
							return pluginState;
					}
				}

				if (tr.docChanged && pluginState.status !== 'idle') {
					const state = pluginState;
					return {
						...state,
						...(state.status === 'loading' ||
						state.status === 'suggestion' ||
						state.status === 'suggestion_pending'
							? { cursorPos: tr.mapping.map(state.cursorPos) }
							: {}),
					} as AiAutocompletePluginState;
				}

				return pluginState;
			},
		},

		// -----------------------------------------------------------------------
		// Decorations
		// -----------------------------------------------------------------------
		props: {
			decorations(state: EditorState) {
				const pluginState = aiAutocompletePluginKey.getState(state);
				if (pluginState?.status !== 'suggestion') {
					return null;
				}
				return buildSuggestionDecoration(state, pluginState);
			},

			handleDOMEvents: {
				blur: (view) => {
					const pluginState = aiAutocompletePluginKey.getState(view.state);
					if (pluginState && pluginState.status !== 'idle') {
						api?.core?.actions?.execute?.(api?.aiAutocomplete?.commands?.dismissSuggestion?.());
					}
					return false;
				},
			},
		},

		// -----------------------------------------------------------------------
		// view() — owns all side effects (fetch lifecycle + shared state resets)
		// -----------------------------------------------------------------------
		view() {
			return {
				update(view, prevState) {
					const newPluginState = aiAutocompletePluginKey.getState(view.state);
					const prevPluginState = aiAutocompletePluginKey.getState(prevState);

					if (newPluginState === prevPluginState) {
						return;
					}

					// Reset shared trigger-line index on idle
					if (newPluginState?.status === 'idle') {
						autoTriggerSharedState.triggerLineIndex = null;
					}

					// Reset trigger-line index when switching to manual loading
					if (
						newPluginState?.status === 'loading' &&
						newPluginState.trigger === 'manual' &&
						prevPluginState?.status !== 'loading'
					) {
						autoTriggerSharedState.triggerLineIndex = null;
					}

					// Entering auto loading: initialise shared state
					if (
						newPluginState?.status === 'loading' &&
						prevPluginState?.status !== 'loading' &&
						newPluginState.trigger === 'auto'
					) {
						autoTriggerSharedState.userPressedEnter = false;
						autoTriggerSharedState.postEnterPlainTextBaseline = null;
						autoTriggerSharedState.triggerLineIndex = get1BasedLineIndexFromDocStart(
							view.state.doc,
							newPluginState.cursorPos,
						);
					}

					// Leaving 'loading' — cancel in-flight fetch
					if (prevPluginState?.status === 'loading' && newPluginState?.status !== 'loading') {
						abortController?.abort();
						abortController = null;
					}

					// Entering 'loading' — fire the API call
					if (newPluginState?.status === 'loading' && prevPluginState?.status !== 'loading') {
						abortController = new AbortController();

						const markdown = serializeDocWithCursorMarker(view, newPluginState.cursorPos);

						callHeadingAutocompleteApi(markdown, abortController.signal)
							.then((suggestion) => {
								const loading = aiAutocompletePluginKey.getState(view.state);
								if (loading?.status !== 'loading') {
									autoTriggerSharedState.userPressedEnter = false;
									autoTriggerSharedState.postEnterPlainTextBaseline = null;
									return;
								}

								const showImmediately =
									loading.trigger === 'auto' && autoTriggerSharedState.userPressedEnter;
								autoTriggerSharedState.userPressedEnter = false;

								// If showing immediately, verify doc hasn't changed beyond the Enter
								if (showImmediately && autoTriggerSharedState.postEnterPlainTextBaseline !== null) {
									const currentPlain = getDocumentPlainText(view.state.doc);
									if (currentPlain !== autoTriggerSharedState.postEnterPlainTextBaseline) {
										autoTriggerSharedState.postEnterPlainTextBaseline = null;
										view.dispatch(
											view.state.tr.setMeta(aiAutocompletePluginKey, {
												type: ACTIONS.DISMISS,
											}),
										);
										return;
									}
								}
								autoTriggerSharedState.postEnterPlainTextBaseline = null;

								const tr = view.state.tr.setMeta(aiAutocompletePluginKey, {
									type: ACTIONS.SET_SUGGESTION,
									suggestion,
									...(showImmediately
										? {
												displaySuggestionImmediately: true,
												suggestionCursorPos: view.state.selection.from,
											}
										: {}),
								});
								if (loading.trigger === 'manual' || showImmediately) {
									addAutocompleteViewedAnalytics(editorAnalyticsAPI)(tr);
								}
								view.dispatch(tr);
							})
							.catch((err: unknown) => {
								if (err instanceof Error && err.name === 'AbortError') {
									return;
								}
								view.dispatch(
									view.state.tr.setMeta(aiAutocompletePluginKey, {
										type: ACTIONS.DISMISS,
									}),
								);
							});
					}
				},

				destroy() {
					abortController?.abort();
					abortController = null;
				},
			};
		},

		// -----------------------------------------------------------------------
		// appendTransaction — doc change → dismiss (accept is handled in command)
		// -----------------------------------------------------------------------
		appendTransaction(transactions, _oldState, newState) {
			const currentPluginState = aiAutocompletePluginKey.getState(newState);

			// Use structural doc equality, not `tr.docChanged`: the latter is true for any
			// step (including AnalyticsStep from attachAnalyticsEvent on invoke), which would
			// incorrectly dismiss loading state before the API runs.
			const documentChanged = transactions.some((tr) => !tr.before.eq(tr.doc));

			// Visible suggestion or pending auto reveal: don't dismiss on doc
			// change — keydown already dismisses on typing before the doc mutates.
			if (
				currentPluginState?.status === 'suggestion' ||
				currentPluginState?.status === 'suggestion_pending'
			) {
				return null;
			}

			// Auto loading: allow doc changes (Enter / same-line edits).
			// Capture a plain-text baseline on the first doc change after the
			// user pressed Enter so we can verify the doc is untouched when the
			// API response arrives.
			if (currentPluginState?.status === 'loading' && currentPluginState.trigger === 'auto') {
				if (
					documentChanged &&
					autoTriggerSharedState.userPressedEnter &&
					autoTriggerSharedState.postEnterPlainTextBaseline === null
				) {
					autoTriggerSharedState.postEnterPlainTextBaseline = getDocumentPlainText(newState.doc);
				}
				return null;
			}

			if (!documentChanged) {
				return null;
			}

			return newState.tr.setMeta(aiAutocompletePluginKey, { type: ACTIONS.DISMISS });
		},
	});
};
